<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/LaisSidebar.vue';
import AccountAreaTabs from 'dashboard/components-next/navigation/AccountAreaTabs.vue';
import ReportAreaTabs from 'dashboard/components-next/navigation/ReportAreaTabs.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import ImudeiParitySurface from 'dashboard/components-next/imudei/ImudeiParitySurface.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import {
  LAIS_APP_FRAME_CLASS,
  LAIS_APP_MAIN_CLASS,
  LAIS_ROUTER_VIEWPORT_CLASS,
} from 'dashboard/helper/laisClassNames';
import {
  isAccountAreaRoute,
  isReportAreaRoute,
} from 'dashboard/helper/laisAreaNavigation';

export default {
  components: {
    NextSidebar,
    AccountAreaTabs,
    ReportAreaTabs,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    ImudeiParitySurface,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      LAIS_APP_FRAME_CLASS,
      LAIS_APP_MAIN_CLASS,
      LAIS_ROUTER_VIEWPORT_CLASS,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    showAccountAreaTabs() {
      return isAccountAreaRoute(this.$route.name);
    },
    showReportAreaTabs() {
      return isReportAreaRoute(this.$route.name);
    },
    showImudeiParitySurface() {
      const parityRoutes = [
        'operacao_imobiliaria_vendas',
        'operacao_imobiliaria_vendas_detalhes',
        'operacao_imobiliaria_vendas_revisar',
        'operacao_imobiliaria_vendas_cadastro',
        'operacao_imobiliaria_vendas_nova',
        'lais_tenants_rent_flows',
        'lais_tenants_rent_flows_status',
        'lais_tenants_rent_flows_status_phase',
        'visitas_index',
        'visita_show',
        'visita_edit',
        'visita_new',
        'operacao_imobiliaria_pos_venda',
        'operacao_imobiliaria_pos_venda_detalhes',
        'operacao_imobiliaria_pos_venda_status',
        'operacao_imobiliaria_financeiro',
        'operacao_imobiliaria_financeiro_detalhes',
        'operacao_imobiliaria_financeiro_status',
        'operacao_imobiliaria_financeiro_status_alterar',
        'operacao_imobiliaria_corretor',
        'operacao_imobiliaria_vendas_comissao',
      ];

      if (parityRoutes.includes(this.$route.name)) return true;

      return (
        (this.$route.name === 'operacao_imobiliaria_financeiro' &&
          Boolean(this.$route.query.screen)) ||
        this.$route.name === 'financeiro_index'
      );
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div :class="LAIS_APP_FRAME_CLASS">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main :class="LAIS_APP_MAIN_CLASS">
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <div :class="LAIS_ROUTER_VIEWPORT_CLASS">
          <AccountAreaTabs v-if="showAccountAreaTabs" />
          <ReportAreaTabs v-else-if="showReportAreaTabs" />
          <ImudeiParitySurface v-if="showImudeiParitySurface" />
          <router-view v-else />
        </div>
        <CommandBar />
        <CopilotLauncher />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
